/**
 * SHER-1043 — requested sampling for a laboratory request.
 * Same shape as the API's ServiceRequestData.requestedSampling.
 */

export type RequestedSampling =
    | { kind: 'routine' }
    | { kind: 'urgent' }
    | { kind: 'datetime'; at: string };

export const DEFAULT_REQUESTED_SAMPLING: RequestedSampling = { kind: 'routine' };

/** Translation ids for the three kinds and the form legend. */
export const REQUESTED_SAMPLING_LABELS = {
    legend: 'hospital.request.requested_sampling.legend',
    routine: 'hospital.request.requested_sampling.routine',
    urgent: 'hospital.request.requested_sampling.urgent',
    datetime: 'hospital.request.requested_sampling.datetime',
    requestDateTime: 'hospital.request.request_datetime',
} as const;

/**
 * Text shown in the table and the PDF. A datetime uses the locale formatter the
 * caller already uses for request dates, so the two columns stay consistent.
 */
export function formatRequestedSampling(
    sampling: RequestedSampling | null | undefined,
    translate: (id: string) => string,
    formatDateTime: (iso: string) => string,
): string {
    if (!sampling || sampling.kind === 'routine') {
        return translate(REQUESTED_SAMPLING_LABELS.routine);
    }
    if (sampling.kind === 'urgent') {
        return translate(REQUESTED_SAMPLING_LABELS.urgent);
    }
    return formatDateTime(sampling.at);
}

export function isRequestedSampling(value: unknown): value is RequestedSampling {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const kind = (value as { kind?: unknown }).kind;
    if (kind === 'routine' || kind === 'urgent') {
        return true;
    }
    if (kind === 'datetime') {
        const at = (value as { at?: unknown }).at;
        return typeof at === 'string' && !Number.isNaN(Date.parse(at));
    }
    return false;
}
